import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { AuditDynamicFormRHFNewProps } from '../../../../types/auditFormTypes';
import { auditDynamicFormSchema } from '../validators/auditDynamicFormSchema';
import {
  buildAuditDynamicFormErrorState,
  buildJsonModeFormData,
  createAuditDynamicFormDefaultValues,
  createEmptyTicketDetail,
} from '../utils/auditDynamicFormUtils';

type UseAuditDynamicFormLogicParams = Pick<
  AuditDynamicFormRHFNewProps,
  'initialValues' | 'onSubmit' | 'onSwitchModes'
>;

export const useAuditDynamicFormLogic = ({
  initialValues,
  onSubmit,
  onSwitchModes,
}: UseAuditDynamicFormLogicParams) => {
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<any>(initialValues);

  useEffect(() => {
    setFormData(initialValues);
  }, [initialValues]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: createAuditDynamicFormDefaultValues(formData),
    resolver: yupResolver(auditDynamicFormSchema),
  });

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'ticket_details',
  });

  const handleAppend = useCallback(() => {
    append(createEmptyTicketDetail());
  }, [append]);

  const handleInsert = useCallback((index: number) => {
    insert(index, createEmptyTicketDetail());
  }, [insert]);

  const handleRemove = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  const handleSwitchToJson = useCallback(() => {
    const currentFormValues = getValues();
    onSwitchModes(buildJsonModeFormData(currentFormValues), 'json');
  }, [getValues, onSwitchModes]);

  const handleReject = useCallback(() => {
    const currentValues = getValues();
    onSubmit({
      ...currentValues,
      action: 'reject',
    });
  }, [getValues, onSubmit]);

  const handleCloseErrorDialog = useCallback(() => {
    setErrorPopupOpen(false);
  }, []);

  const handleFormSubmit = useCallback((data: any) => {
    onSubmit(data);
  }, [onSubmit]);

  const handleSubmitError = useCallback((submitErrors: any) => {
    const currentAction = getValues('action');

    if (currentAction === 'reject') {
      onSubmit(getValues());
      return;
    }

    const currentValues = getValues();
    const { errorMessage: nextErrorMessage } = buildAuditDynamicFormErrorState(
      submitErrors,
      currentValues,
    );

    setErrorMessage(nextErrorMessage);
    setErrorPopupOpen(true);
  }, [getValues, onSubmit]);

  return {
    control,
    handleSubmit,
    errors,
    setValue,
    getValues,
    fields,
    errorPopupOpen,
    errorMessage,
    handleAppend,
    handleInsert,
    handleRemove,
    handleSwitchToJson,
    handleReject,
    handleCloseErrorDialog,
    handleFormSubmit,
    handleSubmitError,
  };
};

export default useAuditDynamicFormLogic;
